import { clearLocalStorage } from "helpers/localStorage";
import moment from "moment";
import { alert, responseMiddleware } from "./alert";

const { protocol, host } = location;
const url = `${protocol}//${host}`;
let tokenRefresh: ReturnType<typeof performTokenRefresh> | undefined;

export type ResponseWithStatus<T> = {
    data: T | false | undefined;
    status: number;
};

type RequestOptions = {
    noContentAlerts?: boolean;
    countInHeaders?: boolean;
    withoutMiddleware?: boolean;
    withoutConnectionAlert?: boolean;
    shouldAlert?: boolean;
    textBody?: boolean;
    withoutErrorAlert?: boolean;
    returnErrorText?: boolean;
    withStatus?: boolean;
    headers?: Record<string, string>;
    alertParams?: { successAlerts: boolean; noContentAlerts: boolean };
};

function options(headers: Record<string, string> = {}) {
    const merged: Record<string, string | null> = {
        "X-Requested-With": "XMLHttpRequest",
        "X-Device-Id": localStorage.getItem("deviceId"),
        "Content-Type": "application/json; charset=utf-8",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
    };
    for (const [name, value] of Object.entries(headers)) {
        const existing = Object.keys(merged).find((key) => key.toLowerCase() === name.toLowerCase());
        merged[existing ?? name] = value;
    }
    return {
        method: "POST",
        cache: "default",
        headers: merged,
    };
}

export function connectionAlert(err: Error, withoutConnectionAlert = false): never {
    if (withoutConnectionAlert) {
        throw err;
    }
    if (err.message === "Failed to fetch") {
        alert({
            type: "error",
            message: "Соединение с сервером потеряно. Попробуйте обновить страницу.",
            title: "Ошибка.",
        });
    } else {
        alert({ type: "error", message: "Неудачный запрос", title: "Ошибка." });
    }
    throw err;
}

export function refreshToken(withoutConnectionAlert = false) {
    if (!tokenRefresh) {
        localStorage.setItem("refreshTokenRunning", "true");
        tokenRefresh = performTokenRefresh(withoutConnectionAlert).finally(() => {
            tokenRefresh = undefined;
            localStorage.removeItem("refreshTokenRunning");
        });
    }
    return tokenRefresh;
}

function performTokenRefresh(withoutConnectionAlert: boolean) {
    const body = {
        AccessToken: localStorage.getItem("token"),
        RefreshToken: localStorage.getItem("refreshToken"),
    };
    return fetch(`${url}/api/account/RefreshToken`, { body: JSON.stringify(body), ...options() } as RequestInit)
        .then((response) => {
            if (!response) {
                connectionAlert(new Error("Failed to fetch"), withoutConnectionAlert);
            }
            return response
                .json()
                .then((nextRes) => {
                    const {
                        accessToken: { token, expiresIn },
                        currentUser,
                        refreshToken,
                    } = nextRes;
                    localStorage.setItem("token", token);
                    localStorage.setItem("refreshToken", refreshToken);
                    localStorage.setItem("tokenExpiresIn", moment().add(expiresIn, "seconds").toISOString());
                    localStorage.setItem("currentUser", JSON.stringify(currentUser));
                    return { token, currentUser };
                })
                .catch((err) => connectionAlert(err, withoutConnectionAlert));
        })
        .catch((err) => {
            clearLocalStorage();
            setTimeout(() => window.location.reload(), 500);
            connectionAlert(err, withoutConnectionAlert);
        });
}

function withCancellation<T>(promise: Promise<T>, signal?: AbortSignal | null): Promise<T> {
    if (!signal) {
        return promise;
    }
    signal.throwIfAborted();
    return new Promise<T>((resolve, reject) => {
        const abort = () => reject(signal.reason);
        signal.addEventListener("abort", abort, { once: true });
        promise.then(resolve, reject).finally(() => signal.removeEventListener("abort", abort));
    });
}

/** Shared authentication transport. JSON interpretation belongs to the caller. */
export async function fetchAuthenticatedResponse(
    resource: string,
    params: RequestInit = {},
    withoutConnectionAlert = true,
    headers?: Record<string, string>,
) {
    params.signal?.throwIfAborted();
    const tokenExpiresIn = localStorage.getItem("tokenExpiresIn");
    if (!(tokenExpiresIn && moment(tokenExpiresIn) > moment())) {
        await withCancellation(refreshToken(withoutConnectionAlert), params.signal);
    }
    params.signal?.throwIfAborted();
    return fetch(`${url}/${resource}`, { ...options(headers), ...params } as RequestInit).catch((error) => {
        if (params.signal?.aborted || error?.name === "AbortError") {
            throw error;
        }
        return connectionAlert(error, withoutConnectionAlert);
    });
}

export function request<T>(
    resource: string,
    params: {},
    reqOptions: RequestOptions & { withStatus: true },
): Promise<ResponseWithStatus<T>>;
export function request(resource: string, params: {}, reqOptions?: RequestOptions): Promise<any>;
export function request(resource: string, params: {}, reqOptions: RequestOptions = {}): Promise<any> {
    const { withoutMiddleware = false, withoutConnectionAlert = false } = reqOptions;
    return fetchAuthenticatedResponse(resource, params, withoutConnectionAlert, reqOptions.headers).then(async (res) => {
        if (withoutMiddleware) {
            return res;
        }
        const data = await responseMiddleware(res, reqOptions);
        return reqOptions.withStatus ? { data, status: res.status } : data;
    });
}

export function alertRequest(
    resource: string,
    params: {},
    alertParams?: { successAlerts: boolean; noContentAlerts: boolean },
) {
    return request(resource, params, { shouldAlert: true, alertParams });
}

export function body(params: {}) {
    return params ? { body: JSON.stringify(params) } : {};
}

export function downloadFileHandler(res: Response, fileName: string) {
    if (res.ok) {
        return res.blob().then((blob) => {
            const link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            link.download = fileName;
            link.click();
        });
    } else {
        res.text().then((x) => console.error(x));
        alert({ message: "Произошла ошибка", type: "warning" });
    }
}
